import type { ParsedUrlQuery } from 'querystring'

import { IncidentFilterCriteria } from '@/graphql/generated/schemas'

type QueryParamValue = string | string[] | undefined
export type DeepLinkIncidentFilterCriteria = {
  facilityIdIn?: IncidentFilterCriteria['facilityIdIn']
}

type FilterCriteriaParser = (
  value: QueryParamValue
) => DeepLinkIncidentFilterCriteria | null

const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

export const parseMultiValueParam = (value: QueryParamValue): string[] => {
  if (value === undefined) return []

  const chunks = Array.isArray(value) ? value : [value]

  const parsedValues = chunks
    .flatMap((chunk) => chunk.split(','))
    .map((entry) => entry.trim())
    .filter((entry) => entry.length > 0)

  return [...new Set(parsedValues)]
}

const parseFacilityIdParam: FilterCriteriaParser = (value) => {
  const facilityIds = parseMultiValueParam(value).filter((id) =>
    UUID_REGEX.test(id)
  )

  return facilityIds.length > 0 ? { facilityIdIn: facilityIds } : null
}

// Deep-link params are parsed through handlers to keep this extensible.
// Add new params by creating a parser and registering it below.
const deepLinkParamParsers: Record<string, FilterCriteriaParser> = {
  facilityId: parseFacilityIdParam,
}

export const parseIncidentsDeepLinkQuery = (
  query: ParsedUrlQuery
): DeepLinkIncidentFilterCriteria | null => {
  const criteria = Object.entries(deepLinkParamParsers).reduce(
    (acc, [paramName, parser]) => {
      const parsedCriteria = parser(query?.[paramName])

      if (!parsedCriteria) return acc
      return {
        ...acc,
        ...parsedCriteria,
      }
    },
    {} as DeepLinkIncidentFilterCriteria
  )

  return Object.keys(criteria).length > 0 ? criteria : null
}
